/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Notification Tray Component
 *
 * Self-contained bell icon + popover panel that polls for unacknowledged
 * notifications and lets the user acknowledge them individually or in bulk.
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { apiFetch } from '../../client/api.js';
import { stateManager } from '../../client/state.js';
import { dispatchTrayCount } from '../../client/tray-count-events.js';
import {
  canShowPushNotification,
  enablePushWithPermission,
  pushPermission,
  setPushOptIn,
  PUSH_PREFERENCE_EVENT,
  type PushPermissionState,
} from '../../client/push-preference.js';
import type { User, Notification } from '../../shared/types.js';
import { formatRelative } from '../../utils/time.js';
import { navigateTo } from '../../client/navigation.js';
import { coalescedRefresh, type CoalescedRefresh } from '../../client/coalesced-refresh.js';

/**
 * A scheduled dispatch blocked by an agent row in phase error. The row's
 * agentId is the errored agent, so "View agent" leads to the delete that
 * unblocks the schedule.
 */
export const SCHEDULE_BLOCKED_STATUS = 'SCHEDULE_BLOCKED';

/**
 * Statuses of the chat rows the hub used to write into the bell (mentions
 * and DMs). It writes none now, but rows made before that can remain.
 */
const RETIRED_CHAT_STATUSES: ReadonlySet<string> = new Set(['MENTION', 'DM_RECEIVED']);

/** The nil UUID some older notification rows carry in place of an agent. */
const NIL_UUID = '00000000-0000-0000-0000-000000000000';

/** Whether a notification row names an agent the "View agent" link can open. */
function hasAgentLink(agentId: string | undefined): boolean {
  return !!agentId && agentId !== NIL_UUID;
}

/**
 * The notification a `notification-created` event carries in full, or null.
 *
 * The user-scoped `user.{id}.notification` subject delivers the row itself
 * (UserNotificationEvent): every field the tray renders (id, status,
 * message, createdAt, agentId). The bare `notification.created` path, and
 * anything malformed, yields null and is fetched instead.
 *
 * The payload has no `acknowledged` field: a notification that was just
 * created is unacknowledged by definition, so a missing field passes and
 * only an explicit `true` is rejected. A payload addressed to another user
 * (`subscriberId`) is rejected too, as a second gate behind the subject.
 */
export function notificationFromEvent(data: unknown, userId: string | null): Notification | null {
  if (!userId || !data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const str = (v: unknown): v is string => typeof v === 'string';
  if (!str(d.id) || !d.id || !str(d.status) || !d.status || !str(d.message)) return null;
  if (!str(d.createdAt) || Number.isNaN(Date.parse(d.createdAt))) return null;
  if (!str(d.agentId)) return null;
  if (d.acknowledged !== undefined && d.acknowledged !== false) return null;
  if (d.subscriberId !== undefined && d.subscriberId !== userId) return null;
  return {
    id: d.id,
    subscriptionId: str(d.subscriptionId) ? d.subscriptionId : '',
    agentId: d.agentId,
    projectId: str(d.projectId) ? d.projectId : '',
    subscriberType: 'user',
    subscriberId: userId,
    status: d.status,
    message: d.message,
    dispatched: false,
    acknowledged: false,
    createdAt: d.createdAt,
  };
}

const POLL_INTERVAL_MS = 5 * 60_000; // 5 minutes — fallback only; SSE delivers in real-time

@customElement('scion-notification-tray')
export class ScionNotificationTray extends LitElement {
  @property({ type: Object })
  user: User | null = null;

  @state() private notifications: Notification[] = [];
  @state() private open = false;

  /** Mirrors the shared push preference so the panel can render its state. */
  @state() private pushEnabled = false;
  @state() private pushPermission: PushPermissionState = 'default';

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  /** The user id loading was started for; null when nothing is running. */
  private activeUserId: string | null = null;
  private boundOnClickOutside = this.onClickOutside.bind(this);
  private boundOnNotification = this.onNotificationEvent.bind(this);
  private boundOnPushPreference = (): void => this.syncPushState();

  /** IDs already seen — used to detect genuinely new notifications. */
  private seenIds = new Set<string>();

  /** Suppresses browser push for the initial fetch so existing notifications don't fire. */
  private initialFetchDone = false;

  /** The user id that the list, seenIds and initialFetchDone belong to. */
  private stateUserId: string | null = null;

  /** Counts list requests sent; each request knows its own number. */
  private fetchSeq = 0;

  /**
   * The number of the newest list request whose response was applied. A
   * response to an older request, landing after it, is stale and ignored.
   */
  private appliedSeq = 0;

  /**
   * Rows an event added, with the fetchSeq when it was added. A list request
   * sent at or before that number may have been answered before the row
   * existed, so its response must not drop the row.
   */
  private upsertedAtSeq = new Map<string, number>();

  /**
   * Fetches for events that carry no usable notification: the first of a
   * burst fetches at once, the rest collapse into one trailing fetch.
   */
  private eventFetch: CoalescedRefresh = coalescedRefresh(() => this.fetchNotifications(), {
    waitMs: 500,
    maxWaitMs: 3000,
    leading: true,
  });

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  override connectedCallback(): void {
    super.connectedCallback();
    this.syncPushState();
    // Keep in step with the profile settings page, which writes the same
    // preference. Two toggles disagreeing about one setting is worse than one.
    window.addEventListener(PUSH_PREFERENCE_EVENT, this.boundOnPushPreference);
    this.syncUser();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopPolling();
    this.stopListeningForNotifications();
    // A reconnect starts afresh, as a first mount does.
    this.activeUserId = null;
    window.removeEventListener(PUSH_PREFERENCE_EVENT, this.boundOnPushPreference);
    document.removeEventListener('click', this.boundOnClickOutside, true);
  }

  override willUpdate(changed: Map<string, unknown>): void {
    // Clear the previous user's state before this render, so no render pairs
    // the new user with the previous user's list.
    if (changed.has('user')) this.resetOnUserChange();
    if (changed.has('notifications')) this.announceCount();
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('user')) this.syncUser();
    this.detectTruncation();
  }

  /**
   * Starts or stops loading for the signed-in user.
   *
   * Keyed on the user id, not the object: connectedCallback and the first
   * updated() both see the same user on mount, and the header can hand over a
   * new object for the same user (an auth refresh). Neither is a reason to
   * fetch again or to restart polling.
   */
  private syncUser(): void {
    const id = this.user?.id ?? null;
    if (id) {
      if (id === this.activeUserId) return;
      // Loading stops on disconnect and restarts in connectedCallback.
      if (!this.isConnected) return;
      this.activeUserId = id;
      void this.fetchNotifications();
      this.startPolling();
      this.listenForNotifications();
    } else {
      this.activeUserId = null;
      this.stopPolling();
      this.stopListeningForNotifications();
      this.notifications = [];
    }
  }

  // ---------------------------------------------------------------------------
  // Per-user state
  // ---------------------------------------------------------------------------

  /**
   * Resets per-user state when the signed-in user id changes, so the next
   * user's first fetch behaves like a first load: it shows only that user's
   * notifications, suppresses push for the ones that already exist, and does
   * not treat the previous user's notifications as seen.
   */
  private resetOnUserChange(): void {
    const id = this.user?.id ?? null;
    if (id === this.stateUserId) return;
    this.stateUserId = id;
    this.notifications = [];
    this.seenIds = new Set();
    this.upsertedAtSeq.clear();
    this.initialFetchDone = false;
    // The next user's first fetch answers any event the previous user's
    // coalescer held, and their first event fetches at once.
    this.eventFetch.cancel();
  }

  /**
   * Tells the header how many items the list now holds. Called for every
   * change to the list, including a clear on a user change or sign-out; a
   * dropped response for a previous user changes nothing, so it announces
   * nothing.
   */
  private announceCount(): void {
    dispatchTrayCount(this, 'notifications', this.notifications.length);
  }

  /**
   * Whether a response to a request started while requestUserId was signed in
   * may be applied. A response for a previous user, or one that arrives after
   * sign-out, is dropped.
   */
  private isForCurrentUser(requestUserId: string | null): boolean {
    return requestUserId !== null && requestUserId === (this.user?.id ?? null);
  }

  // ---------------------------------------------------------------------------
  // Push preference
  // ---------------------------------------------------------------------------

  private syncPushState(): void {
    this.pushPermission = pushPermission();
    // Permission can be revoked in site settings long after the opt-in was
    // stored, so the toggle reflects both halves rather than the flag alone.
    this.pushEnabled = canShowPushNotification('agent');
  }

  /**
   * Turns desktop notifications for agent events on or off. Chat message
   * alerts have their own toggle in profile settings.
   *
   * This is the only path that asks the browser for permission, and it is
   * reachable from chat because the tray is in the chat header. Permission is
   * never requested on load: browsers penalise unprompted requests, and a
   * permission dialog nobody asked for is the reason they do.
   */
  private async handlePushToggle(): Promise<void> {
    if (this.pushEnabled) {
      setPushOptIn('agent', false);
    } else {
      await enablePushWithPermission('agent');
    }
    this.syncPushState();
  }

  private detectTruncation(): void {
    const messages = this.shadowRoot?.querySelectorAll('.notif-message');
    if (!messages) return;
    messages.forEach((el) => {
      const badge = el.parentElement?.querySelector('.truncation-badge') as HTMLElement | null;
      if (!badge) return;
      badge.style.display = el.scrollHeight > el.clientHeight ? 'inline-flex' : 'none';
    });
  }

  // ---------------------------------------------------------------------------
  // Polling
  // ---------------------------------------------------------------------------

  private startPolling(): void {
    this.stopPolling();
    this.pollTimer = setInterval(() => void this.fetchNotifications(), POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Real-time delivery (via shared stateManager SSE connection)
  // ---------------------------------------------------------------------------

  private listenForNotifications(): void {
    stateManager.addEventListener('notification-created', this.boundOnNotification);
  }

  private stopListeningForNotifications(): void {
    stateManager.removeEventListener('notification-created', this.boundOnNotification);
    this.eventFetch.cancel();
  }

  private onNotificationEvent(e: Event): void {
    // The stateManager fires notification-created when a notification SSE
    // event arrives. A user-scoped event carries the whole row: show it
    // without a request. Anything else is fetched, coalesced. Before the
    // first fetch has finished there is no list to add to (and no seen set
    // to tell a new row by), so that fetch is joined by fetching as well.
    const detail = (e as CustomEvent<{ data?: unknown } | undefined>).detail;
    const n = this.initialFetchDone
      ? notificationFromEvent(detail?.data, this.user?.id ?? null)
      : null;
    if (n) {
      this.upsertNotification(n);
      return;
    }
    this.eventFetch.schedule(e.timeStamp > 0 ? e.timeStamp : undefined);
  }

  /** Adds a notification delivered by an event, or replaces its row. */
  private upsertNotification(n: Notification): void {
    // Chat rows no longer belong in the bell (see fetchNotifications).
    if (RETIRED_CHAT_STATUSES.has(n.status)) return;
    if (!this.seenIds.has(n.id)) {
      this.dispatchBrowserNotification(n);
      this.seenIds.add(n.id);
    }
    this.upsertedAtSeq.set(n.id, this.fetchSeq);
    // The list is newest first, as the endpoint returns it.
    const at = this.notifications.findIndex((x) => x.id === n.id);
    this.notifications =
      at < 0 ? [n, ...this.notifications] : this.notifications.map((x, i) => (i === at ? n : x));
  }

  // ---------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------

  private async fetchNotifications(): Promise<void> {
    const requestUserId = this.user?.id ?? null;
    const seq = ++this.fetchSeq;
    try {
      const res = await apiFetch('/api/v1/notifications?acknowledged=false');
      if (!res.ok) return;
      const data = (await res.json()) as Notification[] | null;
      if (!this.isForCurrentUser(requestUserId)) return;
      // A newer request's answer is already shown: this one is older.
      if (seq < this.appliedSeq) return;
      this.appliedSeq = seq;
      // Older chat rows can still be unacknowledged; chat no longer belongs
      // in the bell, so they are neither listed, counted nor popped.
      const incoming = (data ?? []).filter((n) => !RETIRED_CHAT_STATUSES.has(n.status));

      // Detect new notifications (IDs not previously seen) and dispatch
      // browser push for them — but only after the first fetch so we don't
      // blast existing unacknowledged notifications on page load.
      if (this.initialFetchDone) {
        for (const n of incoming) {
          if (!this.seenIds.has(n.id)) {
            this.dispatchBrowserNotification(n);
          }
        }
      }

      // Rows an event added after this request was sent are newer than its
      // answer: keep them (they are already seen). Older ones are answered.
      const incomingIds = new Set(incoming.map((n) => n.id));
      const kept = this.notifications.filter(
        (n) => !incomingIds.has(n.id) && (this.upsertedAtSeq.get(n.id) ?? -1) >= seq
      );
      for (const [id, at] of this.upsertedAtSeq) {
        if (at < seq) this.upsertedAtSeq.delete(id);
      }
      const next = [...kept, ...incoming];

      // Update seen set to current snapshot
      this.seenIds = new Set(next.map((n) => n.id));
      this.initialFetchDone = true;
      this.notifications = next;
    } catch {
      // Silently ignore network errors during polling
    }
  }

  /**
   * Fires a browser Notification if the user has opted in via profile settings
   * and the browser has granted permission.
   */
  private dispatchBrowserNotification(n: Notification): void {
    if (!canShowPushNotification('agent')) {
      return;
    }

    const title = this.browserNotificationTitle(n.status);
    new window.Notification(title, {
      body: n.message,
      tag: n.id, // deduplicate if the same notification is seen again
      icon: '/scion-notification-icon.png',
    });
  }

  private browserNotificationTitle(status: string): string {
    switch (status) {
      case 'COMPLETED':
        return 'Agent Completed';
      case 'WAITING_FOR_INPUT':
        return 'Agent Waiting on Parent';
      case 'LIMITS_EXCEEDED':
        return 'Agent Limits Exceeded';
      case SCHEDULE_BLOCKED_STATUS:
        return 'Schedule Blocked';
      default:
        return 'Scion Notification';
    }
  }

  private async ackOne(id: string): Promise<void> {
    const requestUserId = this.user?.id ?? null;
    try {
      await apiFetch(`/api/v1/notifications/${id}/ack`, { method: 'POST' });
      if (!this.isForCurrentUser(requestUserId)) return;
      this.notifications = this.notifications.filter((n) => n.id !== id);
      this.upsertedAtSeq.delete(id);
    } catch {
      // Ignore
    }
  }

  private async ackAll(): Promise<void> {
    const requestUserId = this.user?.id ?? null;
    try {
      await apiFetch('/api/v1/notifications/ack-all', { method: 'POST' });
      if (!this.isForCurrentUser(requestUserId)) return;
      this.notifications = [];
      this.upsertedAtSeq.clear();
    } catch {
      // Ignore
    }
  }

  // ---------------------------------------------------------------------------
  // Panel toggle & click-outside
  // ---------------------------------------------------------------------------

  private toggle(): void {
    this.open = !this.open;
    if (this.open) {
      // Defer listener so the current click doesn't immediately close
      requestAnimationFrame(() => {
        document.addEventListener('click', this.boundOnClickOutside, true);
      });
      // Refresh on open
      void this.fetchNotifications();
    } else {
      document.removeEventListener('click', this.boundOnClickOutside, true);
    }
  }

  private onClickOutside(e: Event): void {
    const path = e.composedPath();
    if (!path.includes(this)) {
      this.open = false;
      document.removeEventListener('click', this.boundOnClickOutside, true);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private statusIcon(status: string): string {
    switch (status) {
      case 'COMPLETED':
        return 'check-circle-fill';
      case 'WAITING_FOR_INPUT':
        return 'exclamation-circle-fill';
      case 'LIMITS_EXCEEDED':
        return 'x-circle-fill';
      case SCHEDULE_BLOCKED_STATUS:
        return 'calendar-x';
      default:
        return 'info-circle-fill';
    }
  }

  private statusClass(status: string): string {
    switch (status) {
      case 'COMPLETED':
        return 'status-success';
      case 'WAITING_FOR_INPUT':
        return 'status-warning';
      case 'LIMITS_EXCEEDED':
      case SCHEDULE_BLOCKED_STATUS:
        return 'status-danger';
      default:
        return 'status-info';
    }
  }

  private relativeTime(iso: string): string {
    const ms = new Date(iso).getTime();
    if (Number.isNaN(ms)) return '—';
    // A future instant is clock skew between hub and browser.
    if (ms > Date.now()) return 'just now';
    return formatRelative(iso, { style: 'narrow' });
  }

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------

  static override styles = css`
    :host {
      position: relative;
      display: inline-flex;
      align-items: center;
    }

    /* Bell button */
    .bell-btn {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.25rem;
      height: 2.25rem;
      border: none;
      border-radius: 0.5rem;
      background: transparent;
      color: var(--scion-text-muted, #64748b);
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease;
    }

    .bell-btn:hover {
      background: var(--scion-bg-subtle, #f1f5f9);
      color: var(--scion-text, #1e293b);
    }

    .bell-btn sl-icon {
      font-size: 1.25rem;
    }

    /* Badge */
    .badge {
      position: absolute;
      top: 2px;
      right: 2px;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      border-radius: 8px;
      background: var(--scion-danger, #ef4444);
      color: #fff;
      font-size: 0.625rem;
      font-weight: 700;
      line-height: 16px;
      text-align: center;
      pointer-events: none;
    }

    .badge.pulse {
      animation: badge-pulse 2s ease-in-out infinite;
    }

    @keyframes badge-pulse {
      0%,
      100% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.15);
        opacity: 0.85;
      }
    }

    /* Panel */
    .panel {
      position: absolute;
      top: calc(100% + 0.5rem);
      right: 0;
      width: 360px;
      max-height: 480px;
      display: flex;
      flex-direction: column;
      background: var(--scion-surface, #ffffff);
      border: 1px solid var(--scion-border, #e2e8f0);
      border-radius: 0.75rem;
      box-shadow:
        0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -4px rgba(0, 0, 0, 0.1);
      z-index: 1000;
      overflow: hidden;
    }

    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--scion-border, #e2e8f0);
    }

    .panel-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--scion-text, #1e293b);
      margin: 0;
    }

    .mark-all-btn {
      border: none;
      background: transparent;
      color: var(--scion-primary, #3b82f6);
      font-size: 0.75rem;
      font-weight: 500;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      transition: background 0.15s ease;
    }

    .mark-all-btn:hover {
      background: var(--scion-bg-subtle, #f1f5f9);
    }

    .panel-list {
      flex: 1;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    /* Notification item */
    .notif-item {
      display: flex;
      gap: 0.625rem;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--scion-border, #e2e8f0);
      transition: background 0.1s ease;
    }

    .notif-item:last-child {
      border-bottom: none;
    }

    .notif-item:hover {
      background: var(--scion-bg-subtle, #f1f5f9);
    }

    .notif-icon {
      flex-shrink: 0;
      display: flex;
      align-items: flex-start;
      padding-top: 2px;
    }

    .notif-icon sl-icon {
      font-size: 1rem;
    }

    .status-success sl-icon {
      color: var(--scion-success, #22c55e);
    }

    .status-warning sl-icon {
      color: var(--scion-warning, #f59e0b);
    }

    .status-danger sl-icon {
      color: var(--scion-danger, #ef4444);
    }

    .status-info sl-icon {
      color: var(--scion-text-muted, #64748b);
    }

    .notif-body {
      flex: 1;
      min-width: 0;
    }

    .notif-message {
      font-size: 0.8125rem;
      line-height: 1.4;
      color: var(--scion-text, #1e293b);
      word-break: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .truncation-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0 0.375rem;
      margin-top: 0.125rem;
      font-size: 0.6875rem;
      font-weight: 700;
      line-height: 1.25rem;
      border-radius: 0.5rem;
      background: var(--scion-bg-subtle, #f1f5f9);
      color: var(--scion-text-muted, #64748b);
      cursor: pointer;
      letter-spacing: 0.05em;
    }

    .truncation-badge:hover {
      background: var(--scion-border, #e2e8f0);
    }

    .notif-meta {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: 0.25rem;
      font-size: 0.6875rem;
      color: var(--scion-text-muted, #64748b);
    }

    .notif-meta a {
      color: var(--scion-primary, #3b82f6);
      text-decoration: none;
    }

    .notif-meta a:hover {
      text-decoration: underline;
    }

    .mark-read-link {
      border: none;
      background: transparent;
      color: var(--scion-text-muted, #64748b);
      font-size: 0.6875rem;
      cursor: pointer;
      padding: 0;
      transition: color 0.15s ease;
    }

    .mark-read-link:hover {
      color: var(--scion-primary, #3b82f6);
    }

    .scope-indicator {
      display: inline-flex;
      align-items: center;
      gap: 0.125rem;
      font-size: 0.625rem;
      font-weight: 500;
      padding: 0.0625rem 0.3125rem;
      border-radius: 9999px;
      background: var(--scion-bg-subtle, #f1f5f9);
      color: var(--scion-text-muted, #64748b);
      vertical-align: middle;
    }

    .scope-indicator sl-icon {
      font-size: 0.5625rem;
    }

    .panel-footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      border-top: 1px solid var(--scion-border, #e2e8f0);
    }

    .push-toggle {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      border: none;
      background: transparent;
      color: var(--scion-text-muted, #64748b);
      font-size: 0.75rem;
      font-weight: 500;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      transition: background 0.15s ease;
    }

    .push-toggle.on {
      color: var(--scion-primary, #3b82f6);
    }

    .push-toggle:hover:not(:disabled) {
      background: var(--scion-bg-subtle, #f1f5f9);
    }

    .push-toggle:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    .manage-link {
      border: none;
      background: transparent;
      color: var(--scion-primary, #3b82f6);
      font-size: 0.75rem;
      font-weight: 500;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      text-decoration: none;
      transition: background 0.15s ease;
    }

    .manage-link:hover {
      background: var(--scion-bg-subtle, #f1f5f9);
    }

    /* Empty state */
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 2.5rem 1rem;
      gap: 0.75rem;
      color: var(--scion-text-muted, #64748b);
    }

    .empty-state sl-icon {
      font-size: 2rem;
      opacity: 0.4;
    }

    .empty-state span {
      font-size: 0.8125rem;
    }

    /* Mobile */
    @media (max-width: 640px) {
      .panel {
        position: fixed;
        top: auto;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        max-height: 70vh;
        border-radius: 0.75rem 0.75rem 0 0;
      }
    }
  `;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  override render() {
    const count = this.notifications.length;

    return html`
      <button
        class="bell-btn"
        @click=${(): void => this.toggle()}
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded=${this.open}
      >
        <sl-icon name="bell"></sl-icon>
        ${count > 0
          ? html`<span class="badge pulse">${count > 99 ? '99+' : count}</span>`
          : nothing}
      </button>
      ${this.open ? this.renderPanel() : nothing}
    `;
  }

  private renderPanel() {
    const count = this.notifications.length;
    return html`
      <div class="panel" role="dialog" aria-label="Notifications">
        <div class="panel-header">
          <h3 class="panel-title">Notifications</h3>
          ${count > 0
            ? html`<button class="mark-all-btn" @click=${(): void => void this.ackAll()}>
                Mark all read
              </button>`
            : nothing}
        </div>
        <div class="panel-list">
          ${count > 0 ? this.notifications.map((n) => this.renderItem(n)) : this.renderEmpty()}
        </div>
        <div class="panel-footer">
          ${this.renderPushToggle()}
          <a
            href="/profile/settings"
            class="manage-link"
            @click=${(e: Event): void => {
              e.preventDefault();
              this.open = false;
              document.removeEventListener('click', this.boundOnClickOutside, true);
              navigateTo('/profile/settings');
            }}
            >Manage subscriptions</a
          >
        </div>
      </div>
    `;
  }

  /**
   * The agent-event desktop-notification toggle. Lives in the tray because
   * the tray is the one notification surface present on every page, chat
   * included — the profile settings page is not reachable without leaving a
   * conversation. It mirrors the profile page's agent event alerts toggle.
   */
  private renderPushToggle() {
    if (this.pushPermission === 'unsupported') return nothing;

    // Permission alone decides this. A user who opted in and later revoked
    // permission in site settings still has the stored flag set, and reading
    // it here left the button live: the click could not re-prompt (permission
    // is 'denied', not 'default'), so it silently cleared the flag and only
    // the next render admitted the button was blocked all along.
    const blocked = this.pushPermission === 'denied';
    const label = blocked
      ? 'Agent alerts blocked'
      : this.pushEnabled
        ? 'Agent alerts on'
        : 'Agent alerts off';

    return html`
      <button
        class="push-toggle ${this.pushEnabled ? 'on' : ''}"
        role="switch"
        aria-checked=${this.pushEnabled}
        ?disabled=${blocked}
        title=${blocked ? 'Allow notifications in your browser site settings' : label}
        @click=${(): void => void this.handlePushToggle()}
      >
        <sl-icon name=${this.pushEnabled ? 'bell-fill' : 'bell-slash'}></sl-icon>
        ${label}
      </button>
    `;
  }

  private renderItem(n: Notification) {
    return html`
      <div class="notif-item">
        <div class="notif-icon ${this.statusClass(n.status)}">
          <sl-icon name=${this.statusIcon(n.status)}></sl-icon>
        </div>
        <div class="notif-body">
          <div class="notif-message">${n.message}</div>
          <sl-tooltip content=${n.message} hoist>
            <span class="truncation-badge" style="display:none">...</span>
          </sl-tooltip>
          <div class="notif-meta">
            <span>${this.relativeTime(n.createdAt)}</span>
            ${!hasAgentLink(n.agentId)
              ? // Older rows can carry no agent (the nil UUID); a link would
                // 404, so offer none.
                nothing
              : html`<a
                  href="/agents/${n.agentId}"
                  @click=${(e: Event): void => this.navigateToAgent(e, n.agentId)}
                >
                  View agent
                </a>`}
            <button class="mark-read-link" @click=${(): void => void this.ackOne(n.id)}>
              Mark read
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderEmpty() {
    return html`
      <div class="empty-state">
        <sl-icon name="bell-slash"></sl-icon>
        <span>No notifications</span>
      </div>
    `;
  }

  private navigateToAgent(e: Event, agentId: string): void {
    e.preventDefault();
    this.open = false;
    document.removeEventListener('click', this.boundOnClickOutside, true);
    navigateTo(`/agents/${agentId}`);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'scion-notification-tray': ScionNotificationTray;
  }
}
